/**
 * @fileoverview O seletor da conta ativa, no menu do perfil (etapa 5 dos
 * planos): as contas da pessoa, com a Empresa de cada uma quando são de mais
 * de uma Empresa (quem aceitou um convite de outra Empresa). Trocar é o
 * switchCompany do AuthContext: o servidor confere o vínculo e a página abre
 * na tela inicial da conta nova. O erro aparece na tela.
 */

import React, { useContext, useState } from "react";
import { toast } from "react-toastify";
import MenuItem from "@material-ui/core/MenuItem";
import { makeStyles } from "@material-ui/core/styles";

import ModernSelect from "../ModernSelect";
import { AuthContext } from "../../context/Auth/AuthContext";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles((theme) => ({
  select: { "& .MuiOutlinedInput-root": { borderRadius: 10, fontSize: 13 } },
  option: { display: "flex", flexDirection: "column", minWidth: 0 },
  optionCompany: { fontSize: 11.5, color: theme.palette.type === "dark" ? "#A1A1AA" : "#52525B" },
}));

const byCompanyThenName = (a, b) => (a.groupName || "").localeCompare(b.groupName || "") || a.name.localeCompare(b.name);

/** As contas da pessoa: as do vínculo e a principal, que pode não ter vínculo (o admin criado junto com a conta). */
export const accountsOf = (user) => {
  const linked = user?.companies || [];
  if (!user?.companyId || linked.some((account) => account.id === user.companyId)) return linked;
  return [...linked, { id: user.companyId, name: user.company?.name || "", groupName: user.company?.groupName }];
};

const AccountSwitcher = () => {
  const classes = useStyles();
  const { user, switchCompany } = useContext(AuthContext);
  const [switching, setSwitching] = useState(false);
  const accounts = [...accountsOf(user)].sort(byCompanyThenName);
  // A Empresa aparece nas opções quando as contas são de mais de uma; no campo fechado
  // (estreito), só quando duas contas têm o mesmo nome.
  const manyCompanies = new Set(accounts.map((account) => account.groupName || "")).size > 1;
  const labelOf = (account) =>
    account.groupName && accounts.filter((item) => item.name === account.name).length > 1 ? `${account.name} (${account.groupName})` : account.name;

  // Sem conta ativa (o super sem conta), não há o que trocar aqui.
  if (!user?.companyId) return null;

  const change = async (event) => {
    const companyId = Number(event.target.value);
    if (!companyId || companyId === user.companyId) return;
    setSwitching(true);
    try {
      await switchCompany(companyId);
      // A página já vai para a conta nova. Se o navegador perguntar "sair da página?" (alteração
      // não salva) e a pessoa ficar, o seletor volta e o aviso pede para recarregar.
      setTimeout(() => {
        setSwitching(false);
        toast.info(i18n.t("accountSwitcher.reload"));
      }, 3000);
    } catch (err) {
      toastError(err);
      setSwitching(false);
    }
  };

  return (
    <ModernSelect
      className={classes.select}
      fullWidth
      size="small"
      value={user.companyId}
      onChange={change}
      disabled={switching}
      renderValue={(value) => {
        const account = accounts.find((item) => item.id === value);
        return account ? labelOf(account) : "";
      }}
      inputProps={{ "aria-label": i18n.t("accountSwitcher.label") }}
      MenuProps={{ disableScrollLock: true }}
    >
      {accounts.map((account) => (
        <MenuItem key={account.id} value={account.id}>
          <span className={classes.option}>
            <span>{account.name}</span>
            {manyCompanies && account.groupName && <span className={classes.optionCompany}>{account.groupName}</span>}
          </span>
        </MenuItem>
      ))}
    </ModernSelect>
  );
};

export default AccountSwitcher;
